export const GRE_SKILL_LABELS = {
  main_idea: 'Main idea / primary purpose',
  inference: 'Inference',
  vocab_in_context: 'Vocabulary in context',
  sentence_selection: 'Sentence selection / function',
  strengthen_weaken: 'Strengthen / weaken',
  detail: 'Specific detail',
  tone: 'Tone / attitude',
  structure: 'Organization / structure',
};

const STEM_PATTERNS = [
  [/primary purpose|primarily concerned|main idea|mainly about|best title|central claim/i, 'main_idea'],
  [/inferred|can be infer|suggests that|implies that|most likely agree|most reasonably/i, 'inference'],
  [/as used in|most nearly means|in context.{0,40}mean/i, 'vocab_in_context'],
  [/select the sentence|highlighted sentence|primarily in order to|in order to/i, 'sentence_selection'],
  [/strengthen|weaken|undermin|cast doubt|most seriously weaken/i, 'strengthen_weaken'],
  [/tone|attitude|author'?s attitude/i, 'tone'],
  [/organization|structure of the passage/i, 'structure'],
];

const FORMAT_FALLBACK = {
  select_in_passage: 'sentence_selection',
  multiple_choice_multiple: 'inference',
  multiple_choice_single: 'detail',
};

export const inferGreSkill = (questionType, questionText) => {
  const rawType = String(questionType || '').trim();
  if (GRE_SKILL_LABELS[rawType]) {
    return rawType;
  }

  const text = questionText || '';
  for (const [pattern, skill] of STEM_PATTERNS) {
    if (pattern.test(text)) {
      return skill;
    }
  }

  return FORMAT_FALLBACK[rawType] || 'detail';
};

export const greSkillLabel = (skill, fallbackType, fallbackText) => {
  const resolved = skill || inferGreSkill(fallbackType, fallbackText);
  return GRE_SKILL_LABELS[resolved] || resolved.replace(/_/g, ' ');
};

export const formatAnswerLabel = (value, options = []) => {
  if (value == null || value === '') {
    return 'No answer';
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (value.text != null) {
      return String(value.text);
    }
    if (Array.isArray(value) === false && value.index != null && options[value.index] != null) {
      return String(options[value.index]);
    }
  }

  const values = Array.isArray(value) ? value : [value];
  return values
    .map((item) => {
      if (item && typeof item === 'object' && item.text != null) {
        return String(item.text);
      }
      if (typeof item === 'number' && options[item] != null) {
        return String(options[item]);
      }
      if (typeof item === 'string' && /^\d+$/.test(item) && options[Number(item)] != null) {
        return String(options[Number(item)]);
      }
      return String(item);
    })
    .join(', ');
};

export const parseStoredAnswer = (value) => {
  if (typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const toReadingAnswerIndices = (question, answer) => {
  if (answer == null || answer === '') {
    return [];
  }

  const options = question?.options || [];
  const items = Array.isArray(answer) ? answer : [answer];
  const indices = [];

  items.forEach((item) => {
    if (item == null || item === '') {
      return;
    }
    if (typeof item === 'number' && Number.isInteger(item)) {
      indices.push(item);
      return;
    }
    if (typeof item === 'string' && /^-?\d+$/.test(item.trim())) {
      indices.push(Number(item.trim()));
      return;
    }
    const optionIndex = options.findIndex((option) => option === item);
    if (optionIndex >= 0) {
      indices.push(optionIndex);
    }
  });

  return [...new Set(indices)].sort((left, right) => left - right);
};

export const formatReadingScorePercent = (
  correctAnswers,
  totalQuestions,
  fallbackScore = null
) => {
  const total = Number(totalQuestions);
  const correct = Number(correctAnswers);
  if (Number.isFinite(total) && total > 0 && Number.isFinite(correct)) {
    const percent = Math.round((correct / total) * 1000) / 10;
    return Number.isInteger(percent) ? String(percent) : percent.toFixed(1);
  }
  const fallback = Number(fallbackScore);
  if (!Number.isFinite(fallback)) {
    return '0';
  }
  const rounded = Math.round(fallback * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

export const formatReadingTimeSpent = (timeSpentSeconds) => {
  const seconds = Number(timeSpentSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '—';
  }
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  return `${Math.round(seconds / 60)}m`;
};

export const isReadingOptionSelected = (answer, option, optionIndex) => {
  if (Array.isArray(answer)) {
    return (
      answer.includes(option) ||
      answer.includes(optionIndex) ||
      answer.includes(String(optionIndex))
    );
  }
  return (
    answer === option ||
    answer === optionIndex ||
    answer === String(optionIndex)
  );
};

const correctAnswerText = (question) => {
  if (question.type === 'multiple_choice_multiple') {
    return (question.correct_answers || [])
      .map((index) => question.options?.[index])
      .filter(Boolean)
      .join(', ');
  }

  if (typeof question.correct_answer === 'string') {
    const index = question.correct_answer.charCodeAt(0) - 65;
    return question.options?.[index] || question.correct_answer;
  }

  if (Array.isArray(question.correct_answers)) {
    const index = question.correct_answers[0];
    return question.options?.[index] || formatAnswerLabel(question.correct_answers, question.options);
  }

  return formatAnswerLabel(question.correct_answer, question.options);
};

export const buildLocalReadingReview = ({
  testData,
  answers = [],
  checkAnswerCorrectness,
}) => {
  if (!testData?.questions_data?.length) {
    return null;
  }

  const questions = testData.questions_data.map((question, index) => {
    const skill = question.gre_skill || inferGreSkill(question.type, question.question);
    const userAnswer = answers[index];
    const isCorrect = checkAnswerCorrectness
      ? checkAnswerCorrectness(question, userAnswer)
      : false;

    return {
      question_id: question.id ?? index,
      order_in_passage: index + 1,
      question_type: question.type,
      gre_skill: skill,
      gre_skill_label: question.gre_skill_label || greSkillLabel(skill),
      question_text: question.question,
      options: question.options || [],
      user_answer: userAnswer,
      user_answer_display: formatAnswerLabel(userAnswer, question.options),
      correct_answers: question.correct_answers ?? question.correct_answer,
      correct_answers_display: correctAnswerText(question),
      is_correct: isCorrect,
      explanation: question.explanation || '',
    };
  });

  const recorded = questions.filter((question) => question.user_answer != null && question.user_answer !== '');
  const correctCount = questions.filter((question) => question.is_correct).length;
  const skillBuckets = {};

  questions.forEach((question) => {
    if (!skillBuckets[question.gre_skill]) {
      skillBuckets[question.gre_skill] = {
        skill: question.gre_skill,
        label: question.gre_skill_label,
        attempted: 0,
        correct: 0,
      };
    }
    skillBuckets[question.gre_skill].attempted += 1;
    if (question.is_correct) {
      skillBuckets[question.gre_skill].correct += 1;
    }
  });

  const skillBreakdown = Object.values(skillBuckets).map((item) => ({
    ...item,
    accuracy: item.attempted
      ? Math.round((item.correct / item.attempted) * 1000) / 10
      : 0,
  }));

  const strengths = skillBreakdown
    .filter((item) => item.attempted && item.accuracy >= 80)
    .map((item) => item.label);
  const weaknesses = skillBreakdown
    .filter((item) => item.attempted && item.accuracy < 60)
    .map((item) => item.label);

  return {
    passage_id: testData.id,
    passage_title: testData.title,
    topic: testData.topic,
    difficulty: testData.difficulty,
    score_percent: Math.round((correctCount / questions.length) * 100),
    correct_count: correctCount,
    total_questions: questions.length,
    has_recorded_answers: recorded.length > 0,
    questions,
    skill_breakdown: skillBreakdown,
    strengths,
    weaknesses,
    coaching_notes: weaknesses.length
      ? [
          `Rebuild ${weaknesses.join(', ')}: answer only what the passage supports and eliminate choices that overreach.`,
        ]
      : [
          'Keep mapping the author claim, pivot, and evidence before you open the answer choices.',
        ],
  };
};
